import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaceSmileIcon } from '@heroicons/react/24/outline';
import ReactionPicker from './ReactionPicker.component';

export type tReactionButtonProps = {
  onSelect: (emoji: string) => void;
  currentEmoji?: string;
};

export default function ReactionButton(props: tReactionButtonProps) {
  const { onSelect, currentEmoji } = props;
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const handleSelect = useCallback(
    (emoji: string) => {
      onSelect(emoji);
      setIsOpen(false);
    },
    [onSelect],
  );

  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;

    const updatePosition = () => {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top - 8,
        left: rect.left + rect.width / 2,
      });
    };

    updatePosition();

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current?.contains(target) ||
        pickerRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        className={`transition-opacity p-1 rounded-full hover:bg-base-200 ${isOpen ? 'opacity-100 bg-base-200' : 'opacity-0 group-hover:opacity-100'}`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label="React to message"
      >
        <FaceSmileIcon className="w-5 h-5 text-base-content/40" />
      </button>
      {isOpen &&
        createPortal(
          <div
            ref={pickerRef}
            className="fixed z-[9999]"
            style={{
              top: position.top,
              left: position.left,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <ReactionPicker
              onSelect={handleSelect}
              currentEmoji={currentEmoji}
            />
          </div>,
          document.body,
        )}
    </>
  );
}
